import { useState, type ClipboardEvent, type FormEvent } from 'react'
import { useLocation } from 'react-router-dom'
import { Bug, CheckCircle2, ExternalLink, ImagePlus, X } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { FormDialog, FormDialogContent, FormDialogTrigger } from '@/components/ui/form-dialog'
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { apiClient } from '@/lib/api/client'
import type { FeedbackReportResult } from '@/lib/api/types'
import { HELM_BUILD } from '@/lib/env'
import { EnumSelect } from '@/components/ui/enum-select'
import { diagnosticsSnapshot } from '@/src/lib/diagnostics'

type Kind = 'BUG' | 'IDEA'
type Impact = 'BLOCKING' | 'ANNOYING' | 'MINOR'

const IMPACT_OPTIONS: Array<{ value: Impact; label: string }> = [
  { value: 'BLOCKING', label: 'It stops my work' },
  { value: 'ANNOYING', label: 'I can work around it' },
  { value: 'MINOR', label: 'Small thing' },
]

const MAX_SCREENSHOT = 5 * 1024 * 1024

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

/**
 * "Report a problem": a short form that files a Linear issue with what the
 * person saw plus the page, browser and recent errors, so whoever fixes it
 * (a person or an engineering agent) starts with what they need.
 */
export function ReportProblem() {
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState<Kind>('BUG')
  const [title, setTitle] = useState('')
  const [whatHappened, setWhatHappened] = useState('')
  const [expected, setExpected] = useState('')
  const [steps, setSteps] = useState('')
  const [impact, setImpact] = useState<Impact>('ANNOYING')
  const [screenshot, setScreenshot] = useState<{ name: string; dataUrl: string } | null>(null)
  const [includeDetails, setIncludeDetails] = useState(true)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<FeedbackReportResult | null>(null)

  const snapshot = open ? diagnosticsSnapshot() : null

  function reset() {
    setKind('BUG')
    setTitle('')
    setWhatHappened('')
    setExpected('')
    setSteps('')
    setImpact('ANNOYING')
    setScreenshot(null)
    setIncludeDetails(true)
    setErrors({})
    setError(null)
    setResult(null)
  }

  async function attach(file: File | null | undefined) {
    if (!file) return
    if (!/^image\/(png|jpeg|webp)$/.test(file.type)) {
      setErrors((current) => ({ ...current, screenshot: 'Use a PNG, JPEG or WebP image.' }))
      return
    }
    if (file.size > MAX_SCREENSHOT) {
      setErrors((current) => ({ ...current, screenshot: 'The image is over 5 MB.' }))
      return
    }
    setErrors(({ screenshot: _removed, ...rest }) => rest)
    setScreenshot({ name: file.name || 'screenshot.png', dataUrl: await readAsDataUrl(file) })
  }

  function onPaste(event: ClipboardEvent) {
    const image = Array.from(event.clipboardData.files).find((file) =>
      file.type.startsWith('image/'),
    )
    if (image) {
      event.preventDefault()
      void attach(image)
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    const found: Record<string, string> = {}
    if (title.trim().length < 3) found.title = 'Give it a short title.'
    if (whatHappened.trim().length < 3) {
      found.what = kind === 'BUG' ? 'Say what happened.' : 'Describe the idea.'
    }
    setErrors(found)
    if (Object.keys(found).length) {
      document.getElementById(found.title ? 'report-title' : 'report-what')?.focus()
      return
    }
    setBusy(true)
    setError(null)
    try {
      const details = includeDetails ? diagnosticsSnapshot() : null
      const filed = await apiClient.reportProblem({
        kind,
        title: title.trim(),
        what_happened: whatHappened.trim(),
        expected: expected.trim() || null,
        steps: kind === 'BUG' ? steps.trim() || null : null,
        impact: kind === 'BUG' ? impact : null,
        page: {
          url: window.location.href,
          path: location.pathname,
          title: document.title,
        },
        context: details ? { ...details, helm_build: HELM_BUILD } : { helm_build: HELM_BUILD },
        screenshot: screenshot
          ? { data_url: screenshot.dataUrl, file_name: screenshot.name }
          : null,
      })
      setResult(filed)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The report could not be sent')
    } finally {
      setBusy(false)
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) reset()
      }}
    >
      <FormDialogTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Report a problem" data-tour="header-report">
          <Bug className="h-5 w-5" />
        </Button>
      </FormDialogTrigger>
      {result ? (
        <FormDialogContent
          title="Thanks, it's reported"
          description="It's in the engineering queue with the page and recent errors attached."
          submitLabel="Report something else"
          onSubmit={(event) => {
            event.preventDefault()
            reset()
          }}
        >
          <Alert role="status">
            <CheckCircle2 className="h-4 w-4" />
            <AlertDescription>
              {result.linear_url ? (
                <span>
                  Filed as{' '}
                  <a
                    href={result.linear_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 font-medium underline underline-offset-4"
                  >
                    {result.linear_identifier}
                    <ExternalLink className="size-3.5" />
                  </a>
                  .
                </span>
              ) : (
                'Saved in Helm. It will appear in Linear shortly.'
              )}
            </AlertDescription>
          </Alert>
        </FormDialogContent>
      ) : (
        <FormDialogContent
          title="Report a problem"
          description="Tell us what went wrong, or what would help. It goes straight to engineering."
          submitLabel="Send report"
          busy={busy}
          busyLabel="Sending"
          error={error}
          errorTitle="Not sent"
          onSubmit={submit}
          size="lg"
        >
          <FieldGroup onPaste={onPaste}>
            <Tabs value={kind} onValueChange={(value) => setKind(value as Kind)}>
              <TabsList aria-label="What kind of report">
                <TabsTrigger value="BUG">Something's wrong</TabsTrigger>
                <TabsTrigger value="IDEA">An idea</TabsTrigger>
              </TabsList>
            </Tabs>

            <Field data-invalid={Boolean(errors.title)}>
              <FieldLabel htmlFor="report-title" required>
                Title
              </FieldLabel>
              <Input
                id="report-title"
                value={title}
                maxLength={120}
                placeholder={kind === 'BUG' ? 'Saving a quote fails' : 'Bulk-assign enquiries'}
                onChange={(event) => setTitle(event.target.value)}
                aria-invalid={Boolean(errors.title)}
              />
              <FieldError>{errors.title}</FieldError>
            </Field>

            <Field data-invalid={Boolean(errors.what)}>
              <FieldLabel htmlFor="report-what" required>
                {kind === 'BUG' ? 'What happened?' : 'What would help?'}
              </FieldLabel>
              <Textarea
                id="report-what"
                rows={4}
                value={whatHappened}
                onChange={(event) => setWhatHappened(event.target.value)}
                aria-invalid={Boolean(errors.what)}
              />
              <FieldError>{errors.what}</FieldError>
            </Field>

            <Field>
              <FieldLabel htmlFor="report-expected">
                {kind === 'BUG' ? 'What did you expect?' : 'Why does it matter?'}
              </FieldLabel>
              <Textarea
                id="report-expected"
                rows={2}
                value={expected}
                onChange={(event) => setExpected(event.target.value)}
              />
            </Field>

            {kind === 'BUG' && (
              <>
                <Field>
                  <FieldLabel htmlFor="report-steps">How can we see it happen?</FieldLabel>
                  <Textarea
                    id="report-steps"
                    rows={3}
                    placeholder={'1. Open an inquiry\n2. Click Save quote\n3. …'}
                    value={steps}
                    onChange={(event) => setSteps(event.target.value)}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="report-impact">How much does it get in the way?</FieldLabel>
                  <EnumSelect
                    triggerProps={{ id: 'report-impact', className: 'w-full' }}
                    value={impact}
                    onValueChange={(value) => setImpact(value as Impact)}
                    options={IMPACT_OPTIONS}
                  />
                </Field>
              </>
            )}

            <Field data-invalid={Boolean(errors.screenshot)}>
              <FieldLabel htmlFor="report-screenshot">Screenshot</FieldLabel>
              {screenshot ? (
                <div className="flex items-center gap-3 rounded-md border p-2">
                  <img
                    src={screenshot.dataUrl}
                    alt="Attached screenshot"
                    className="h-14 w-20 rounded object-cover"
                  />
                  <span className="min-w-0 flex-1 truncate text-sm">{screenshot.name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Remove the screenshot"
                    onClick={() => setScreenshot(null)}
                  >
                    <X />
                  </Button>
                </div>
              ) : (
                <label
                  htmlFor="report-screenshot"
                  className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed p-3 text-sm text-muted-foreground hover:bg-accent"
                >
                  <ImagePlus className="size-4" />
                  Paste an image here, or choose one
                </label>
              )}
              <input
                id="report-screenshot"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="sr-only"
                onChange={(event) => {
                  void attach(event.target.files?.[0])
                  event.target.value = ''
                }}
              />
              <FieldError>{errors.screenshot}</FieldError>
            </Field>

            <Field orientation="horizontal">
              <input
                id="report-details"
                type="checkbox"
                className="size-4"
                checked={includeDetails}
                onChange={(event) => setIncludeDetails(event.target.checked)}
              />
              <div>
                <FieldLabel htmlFor="report-details">Include technical details</FieldLabel>
                <FieldDescription>
                  This page ({location.pathname}), your browser, and{' '}
                  {snapshot
                    ? `${snapshot.console_errors.length} recent error${snapshot.console_errors.length === 1 ? '' : 's'} and ${snapshot.failed_requests.length} failed request${snapshot.failed_requests.length === 1 ? '' : 's'}`
                    : 'recent errors'}
                  . No passwords or form contents.
                </FieldDescription>
              </div>
            </Field>
          </FieldGroup>
        </FormDialogContent>
      )}
    </FormDialog>
  )
}
